import { Badge, Tooltip, type BadgeProps } from '@mantine/core';

const DEFAULT_MAX_CHARS = 24;

/**
 * The platform's status pill: a Mantine Badge that can never silently
 * lose its meaning. Mantine ellipsises a squeezed badge ("Pendi…")
 * with no way to read the rest, so every StatusBadge carries a tooltip
 * with the full label — and genuinely long labels are additionally
 * char-capped so one pill can't stretch a table column.
 *
 * Use this for every status-ish pill (report status, approval state,
 * request status, partner status…); plain Badge stays fine for short
 * fixed literals like "Expedited".
 */
export function StatusBadge({
  label,
  tooltip,
  maxChars = DEFAULT_MAX_CHARS,
  size = 'sm',
  variant = 'light',
  ...badgeProps
}: Readonly<
  {
    label: string;
    /** Tooltip content when it should say more than the label. */
    tooltip?: string;
    /** Hard cap before the label itself is truncated with an ellipsis. */
    maxChars?: number;
  } & Omit<BadgeProps, 'children'>
>) {
  const truncated = label.length > maxChars ? `${label.slice(0, maxChars - 1).trimEnd()}…` : label;
  return (
    // duration 0: a status readout, not a flourish — and no dangling
    // transition timer to outlive a jsdom test environment.
    <Tooltip label={tooltip ?? label} withArrow transitionProps={{ duration: 0 }}>
      <Badge size={size} variant={variant} style={{ maxWidth: '100%' }} {...badgeProps}>
        {truncated}
      </Badge>
    </Tooltip>
  );
}
